/**
 * On-call resolution in the browser.
 *
 * `GET /schedules/:id/calendar` is the authority and `spansFromCalendar` adapts
 * its answer. This module is the fallback: it resolves shifts, rotations and
 * overrides locally with the same rules the worker uses (docs/02 section 3.4,
 * packages/db Shift/Override), so the month view still draws when the request
 * fails and so a range wider than the API will serve can still be sketched.
 *
 * All arithmetic is done in the schedule's IANA timezone - shift times are wall
 * clock in that zone, so a DST change must not move the hand-over.
 */
import type { CalendarEntry, Override, Schedule, Shift, UserRef } from './schemas.js';

/** One block of cover, however it was resolved. */
export interface OnCallSpan {
  start: string;
  end: string;
  user: UserRef | null;
  shift_id: string | null;
  shift_name: string | null;
  override_id: string | null;
}

export interface ResolvedSpan extends OnCallSpan {
  /** Rotation slot the participant came from, for tooltips. */
  participant_index: number;
}

/**
 * The API's calendar entries, as spans. An entry can name several people (a
 * split override), and each becomes its own span so the calendar can show them
 * side by side.
 */
export function spansFromCalendar(entries: CalendarEntry[]): ResolvedSpan[] {
  const spans: ResolvedSpan[] = [];
  for (const entry of entries) {
    if (entry.source === 'none') continue;
    const users: (UserRef | null)[] = entry.users.length > 0 ? entry.users : [null];
    users.forEach((user, index) => {
      spans.push({
        start: entry.starts_at,
        end: entry.ends_at,
        user,
        shift_id: entry.shift_id,
        shift_name: entry.shift_name,
        override_id: entry.source === 'override' ? (entry.shift_id ?? entry.schedule_id) : null,
        participant_index: index,
      });
    });
  }
  return spans.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
}

const DAY_MS = 86_400_000;

/** Offset, in milliseconds, of `timeZone` from UTC at the given instant. */
export function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(instant);
    const get = (type: string): number => Number(parts.find((p) => p.type === type)?.value ?? '0');
    const hour = get('hour') % 24;
    const asUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      hour,
      get('minute'),
      get('second'),
    );
    return asUtc - instant.getTime();
  } catch {
    return 0;
  }
}

/** Wall-clock `y-m-d hh:mm` in `timeZone` -> the matching UTC instant. */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string,
): Date {
  const guess = Date.UTC(year, month - 1, day, hour, minute);
  const firstPass = guess - timeZoneOffsetMs(new Date(guess), timeZone);
  return new Date(guess - timeZoneOffsetMs(new Date(firstPass), timeZone));
}

/** Calendar day (in `timeZone`) that contains the instant, as `[y, m, d]`. */
export function zonedDateParts(instant: Date, timeZone: string): [number, number, number] {
  const shifted = new Date(instant.getTime() + timeZoneOffsetMs(instant, timeZone));
  return [shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate()];
}

function parseHhMm(value: string): [number, number] {
  const [h, m] = value.split(':');
  return [Number(h ?? 0) || 0, Number(m ?? 0) || 0];
}

/** Days since 1970-01-01 of a calendar date. */
function dayNumber(year: number, month: number, day: number): number {
  return Math.floor(Date.UTC(year, month - 1, day) / DAY_MS);
}

/** Monday-first week number of a calendar date (1970-01-01 was a Thursday). */
function weekNumber(year: number, month: number, day: number): number {
  return Math.floor((dayNumber(year, month, day) + 3) / 7);
}

/** ISO weekday (Monday 1 ... Sunday 7) of a calendar date. */
export function isoWeekday(year: number, month: number, day: number): number {
  return ((new Date(Date.UTC(year, month - 1, day)).getUTCDay() + 6) % 7) + 1;
}

/**
 * Whole rotation periods between the anchor and a day, counted on the calendar
 * in the schedule's zone the way the worker counts them (packages/core
 * `rotationIndex`): weeks hand over on Monday and months on the 1st whatever
 * the anchor's weekday, `daily` and `custom` count calendar days, so a DST
 * change never moves a hand-over.
 */
function periodsSince(shift: Shift, day: [number, number, number], timeZone: string): number {
  const anchor = zonedDateParts(new Date(shift.rotation_start), timeZone);
  switch (shift.rotation_type) {
    case 'weekly':
      return weekNumber(...day) - weekNumber(...anchor);
    case 'monthly':
      return day[0] * 12 + day[1] - (anchor[0] * 12 + anchor[1]);
    case 'daily':
    case 'custom':
    default:
      return dayNumber(...day) - dayNumber(...anchor);
  }
}

function participantRef(participant: string | UserRef, users: Map<string, UserRef>): UserRef {
  if (typeof participant === 'string') {
    return users.get(participant) ?? { id: participant, display_name: participant, email: '' };
  }
  return users.get(participant.id) ?? participant;
}

/** Which participant of `shift` is on call on the day (in `timeZone`) containing `dayStart`. */
export function rotationIndexFor(shift: Shift, dayStart: Date, timeZone = 'UTC'): number {
  const count = shift.participants.length;
  if (count === 0) return -1;
  if (!Number.isFinite(Date.parse(shift.rotation_start))) return 0;
  const periods = periodsSince(shift, zonedDateParts(dayStart, timeZone), timeZone);
  const slot = Math.floor(periods / Math.max(1, shift.rotation_length));
  return ((slot % count) + count) % count;
}

export interface ResolveOptions {
  schedule: Schedule;
  overrides?: Override[];
  users?: UserRef[];
  from: Date;
  to: Date;
}

/**
 * Every on-call span the schedule produces between `from` and `to`,
 * with overrides applied. Spans are ordered by start.
 */
export function resolveOnCall({
  schedule,
  overrides = [],
  users = [],
  from,
  to,
}: ResolveOptions): ResolvedSpan[] {
  const tz = schedule.timezone || 'UTC';
  const userMap = new Map(users.map((u) => [u.id, u]));
  const excluded = new Set(schedule.excluded_weekdays);
  const scheduleStart = Date.parse(`${schedule.start_date}T00:00:00Z`);
  const scheduleEnd = schedule.end_date ? Date.parse(`${schedule.end_date}T23:59:59Z`) : null;
  const spans: ResolvedSpan[] = [];

  // Walk one day either side so spans that begin the previous evening are kept.
  for (let cursor = from.getTime() - DAY_MS; cursor <= to.getTime() + DAY_MS; cursor += DAY_MS) {
    const [year, month, day] = zonedDateParts(new Date(cursor), tz);
    const dayStart = zonedTimeToUtc(year, month, day, 0, 0, tz);
    // ISO numbering (Monday 1 ... Sunday 7), as the API stores and the worker reads.
    const weekday = isoWeekday(year, month, day);
    if (excluded.has(weekday)) continue;
    if (Number.isFinite(scheduleStart) && dayStart.getTime() + DAY_MS <= scheduleStart) continue;
    if (scheduleEnd !== null && dayStart.getTime() > scheduleEnd) continue;

    for (const shift of schedule.shifts) {
      if (shift.weekdays.length > 0 && !shift.weekdays.includes(weekday)) continue;
      const index = rotationIndexFor(shift, dayStart, tz);
      if (index < 0) continue;
      const [sh, sm] = parseHhMm(shift.start_time);
      const [eh, em] = parseHhMm(shift.end_time);
      const start = zonedTimeToUtc(year, month, day, sh, sm, tz);
      let end = zonedTimeToUtc(year, month, day, eh, em, tz);
      if (end.getTime() <= start.getTime()) end = new Date(end.getTime() + DAY_MS);
      if (end.getTime() <= from.getTime() || start.getTime() >= to.getTime()) continue;
      const base = participantRef(shift.participants[index]!, userMap);
      const override = overrides.find(
        (o) =>
          (o.shift_id === null || o.shift_id === shift.id) &&
          Date.parse(o.starts_at) < end.getTime() &&
          Date.parse(o.ends_at) > start.getTime(),
      );
      spans.push({
        start: start.toISOString(),
        end: end.toISOString(),
        user: override ? participantRef(override.user_id, userMap) : base,
        shift_id: shift.id,
        shift_name: shift.name,
        override_id: override?.id ?? null,
        participant_index: index,
      });
    }
  }
  return spans.sort((a, b) => Date.parse(a.start) - Date.parse(b.start));
}

/** The spans covering a single instant (usually zero or one). */
export function onCallAt(spans: ResolvedSpan[], at: Date): ResolvedSpan[] {
  const t = at.getTime();
  return spans.filter((s) => Date.parse(s.start) <= t && Date.parse(s.end) > t);
}

/** Bucket spans by the calendar day (in `timeZone`) their start falls on. */
export function groupSpansByDay(
  spans: ResolvedSpan[],
  timeZone: string,
): Map<string, ResolvedSpan[]> {
  const out = new Map<string, ResolvedSpan[]>();
  for (const span of spans) {
    const [y, m, d] = zonedDateParts(new Date(span.start), timeZone);
    const key = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const bucket = out.get(key);
    if (bucket) bucket.push(span);
    else out.set(key, [span]);
  }
  return out;
}

/** The 6x7 grid of days a month view renders, Monday first. */
export function monthGrid(year: number, month: number): Date[] {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const offset = (first.getUTCDay() + 6) % 7; // Monday = 0
  const start = new Date(first.getTime() - offset * DAY_MS);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * DAY_MS));
}

export function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}
